<template>
  <div class="flex gap-2">
    <!-- Day Field -->
    <div class="flex-1" :class="dayOrderClass">
      <USelect
        v-model="day"
        :options="dayOptions"
        :placeholder="$t('general.day')"
        :disabled="disabled"
        size="lg"
      />
    </div>
    
    <!-- Month Field -->
    <div class="flex-1" :class="monthOrderClass">
      <USelect
        v-model="month"
        :options="monthOptions"
        :placeholder="$t('general.month')"
        :disabled="disabled"
        size="lg"
      />
    </div>
    
    <!-- Year Field -->
    <div class="flex-1" :class="yearOrderClass">
      <USelect
        v-model="year"
        :options="yearOptions"
        :placeholder="$t('general.year')"
        :disabled="disabled"
        size="lg"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
defineOptions({
  name: 'BDateInput'
});

const model = defineModel<string | number | Date | undefined>({
  required: true,
  default: ''
});

const props = withDefaults(defineProps<{
  disabled?: boolean;
  augmenter?: 'timestamp' | 'ymd' | 'date';
  minDate?: Date;
  maxDate?: Date;
}>(), {
  augmenter: 'ymd'
});

const { $i18n } = useNuxtApp();

const day = ref();
const month = ref();
const year = ref();

const dayOrderClass = computed(() => {
  const locale = String($i18n.locale?.value || 'en');
  
  if (locale.startsWith('en-US')) {
    return 'order-2'; // Day second in US format (M/D/Y)
  } else if (locale.startsWith('ja') || locale.startsWith('hu') || locale.startsWith('fa')) {
    return 'order-3'; // Day last in Y/M/D format
  } else {
    return 'order-1'; // Day first in D/M/Y format
  }
});

const monthOrderClass = computed(() => {
  const locale = String($i18n.locale?.value || 'en');
  
  if (locale.startsWith('en-US')) {
    return 'order-1'; // Month first in US format (M/D/Y)
  } else if (locale.startsWith('ja') || locale.startsWith('hu') || locale.startsWith('fa')) {
    return 'order-2'; // Month second in Y/M/D format
  } else {
    return 'order-2'; // Month second in D/M/Y format
  }
});

const yearOrderClass = computed(() => {
  const locale = String($i18n.locale?.value || 'en');
  
  if (locale.startsWith('en-US')) {
    return 'order-3'; // Year last in US format (M/D/Y)
  } else if (locale.startsWith('ja') || locale.startsWith('hu') || locale.startsWith('fa')) {
    return 'order-1'; // Year first in Y/M/D format
  } else {
    return 'order-3'; // Year last in D/M/Y format
  }
});


const getDaysInMonth = (month: number, year?: number) => {
  if (!year) {
    // Conservative approach when year is not set
    const daysInMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    return daysInMonth[month - 1] || 31;
  }
  return new Date(year, month, 0).getDate();
};

const minDateParsed = computed(() => props.minDate || null);
const maxDateParsed = computed(() => props.maxDate || null);

const dayOptions = computed(() => {
  let maxDays = 31;
  if (month.value) {
    const yearNum = year.value ? parseInt(year.value) : undefined;
    maxDays = getDaysInMonth(parseInt(month.value), yearNum);
  }
  
  const selectedYear = year.value ? parseInt(year.value) : null;
  const selectedMonth = month.value ? parseInt(month.value) : null;
  const options = [];
  
  for (let i = 1; i <= maxDays; i++) {
    let includeDay = true;
    
    if (selectedYear && selectedMonth && minDateParsed.value) {
      const minYear = minDateParsed.value.getFullYear();
      const minMonth = minDateParsed.value.getMonth() + 1;
      const minDay = minDateParsed.value.getDate();
      
      if (selectedYear === minYear && selectedMonth === minMonth && i < minDay) {
        includeDay = false;
      }
    }
    
    if (selectedYear && selectedMonth && maxDateParsed.value) {
      const maxYear = maxDateParsed.value.getFullYear();
      const maxMonth = maxDateParsed.value.getMonth() + 1;
      const maxDay = maxDateParsed.value.getDate();
      
      if (selectedYear === maxYear && selectedMonth === maxMonth && i > maxDay) {
        includeDay = false;
      }
    }
    
    if (includeDay) {
      options.push({
        value: String(i).padStart(2, '0'),
        label: String(i)
      });
    }
  }
  
  return options;
});

const monthOptions = computed(() => {
  const selectedYear = year.value ? parseInt(year.value) : null;
  const options = [];
  
  for (let i = 0; i < 12; i++) {
    const monthValue = i + 1;
    let includeMonth = true;
    
    if (selectedYear && minDateParsed.value) {
      const minYear = minDateParsed.value.getFullYear();
      const minMonth = minDateParsed.value.getMonth() + 1;
      if (selectedYear === minYear && monthValue < minMonth) {
        includeMonth = false;
      }
    }
    
    if (selectedYear && maxDateParsed.value) {
      const maxYear = maxDateParsed.value.getFullYear();
      const maxMonth = maxDateParsed.value.getMonth() + 1;
      if (selectedYear === maxYear && monthValue > maxMonth) {
        includeMonth = false;
      }
    }
    
    if (includeMonth) {
      options.push({
        value: String(monthValue).padStart(2, '0'),
        label: new Date(2000, i).toLocaleDateString($i18n.locale.value || 'en', { month: 'long' })
      });
    }
  }
  
  return options;
});

const yearOptions = computed(() => {
  const currentYear = new Date().getFullYear();
  const minYear = minDateParsed.value ? minDateParsed.value.getFullYear() : currentYear - 119;
  const maxYear = maxDateParsed.value ? maxDateParsed.value.getFullYear() : currentYear + 2;
  
  const options = [];
  for (let year = maxYear; year >= minYear; year--) {
    options.push({
      value: String(year),
      label: String(year)
    });
  }
  
  return options;
});

// Watch for changes in month to validate day
watch(month, (newMonth) => {
  if (day.value && newMonth) {
    const yearNum = year.value ? parseInt(year.value) : undefined;
    const maxDays = getDaysInMonth(parseInt(newMonth), yearNum);
    if (parseInt(day.value) > maxDays) {
      day.value = '';
    }
  }
  
  // Also check if selected month/day is still valid with min/max constraints
  if (year.value && month.value && !monthOptions.value.find(opt => opt.value === month.value)) {
    month.value = '';
    day.value = '';
  }
});

// Watch for changes in year to validate month and day
watch(year, (newYear) => {
  if (newYear) {
    // Check if selected month is still valid
    if (month.value && !monthOptions.value.find(opt => opt.value === month.value)) {
      month.value = '';
      day.value = '';
    } else if (day.value && month.value) {
      // Check if selected day is still valid
      const maxDays = getDaysInMonth(parseInt(month.value), parseInt(newYear));
      if (parseInt(day.value) > maxDays) {
        day.value = '';
      } else if (!dayOptions.value.find(opt => opt.value === day.value)) {
        day.value = '';
      }
    }
  }
});

// Watch for any field changes to update model value
watch([day, month, year], () => {
  updateModelValue();
});

const updateModelValue = () => {
  if (day.value && month.value && year.value) {
    if (props.augmenter === 'timestamp') {
      const date = new Date(year.value, month.value - 1, day.value);
      const offset = date.getTimezoneOffset() * 60 * 1000;
      model.value = (date.getTime() - offset) / 1000;
    } else if (props.augmenter === 'ymd') {
      model.value = `${year.value}${month.value.padStart(2, '0')}${day.value.padStart(2, '0')}`;
    } else if (props.augmenter === 'date') {
      model.value = new Date(parseInt(year.value), parseInt(month.value) - 1, parseInt(day.value));
    } else {
      model.value = `${year.value}${month.value.padStart(2, '0')}${day.value.padStart(2, '0')}`;
    }
  } else {
    model.value = '';
  }
};

const parseModelValue = (value: string | number | Date | undefined) => {
  if (!value) {
    day.value = '';
    month.value = '';
    year.value = '';
    return;
  }

  if (value instanceof Date) {
    day.value = value.getDate().toString().padStart(2, '0');
    month.value = (value.getMonth() + 1).toString().padStart(2, '0');
    year.value = value.getFullYear().toString();
  } else if (props.augmenter === 'timestamp') {
    const date = new Date(parseInt(value.toString()) * 1000);

    day.value = date.getDate().toString().padStart(2, '0');
    month.value = (date.getMonth() + 1).toString().padStart(2, '0');
    year.value = date.getFullYear().toString();
  } else if (props.augmenter === 'ymd') {
    const _value = value.toString();

    year.value = _value.substring(0, 4);
    month.value = _value.substring(4, 6);
    day.value = _value.substring(6, 8);
  } else {
    day.value = '';
    month.value = '';
    year.value = '';
  }
};

onMounted(() => {
  parseModelValue(model.value);
});
</script>